import {
  ChatBubbleOvalLeftEllipsisIcon,
  DocumentArrowUpIcon,
  ListBulletIcon,
  SwatchIcon,
} from "@heroicons/react/24/outline";
import { Handle, Position } from "reactflow";
import { Badge } from "../externalComponents/tailwind/badge";
import { Text } from "../externalComponents/tailwind/text";
import { formatParagraph } from "../functions/formatParagraph";
import { useEffect, useRef } from "react";

const handleIds = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j"];

function MessageNode({ id, data, selected }) {
  const videoRef = useRef(null);

  useEffect(() => {
    videoRef.current?.load();
  }, [data.video]);

  return (
    <div
      className={
        (selected ? "selected " : "") +
        (data.isWaMeLink ? "wa-me-link " : "") +
        (data.isTemplate ? "template-node" : "message-node")
      }
      onClick={() => data.clickNode(id)}
    >
      {data.isTemplate ? null : (
        <Handle type="target" position={Position.Left} />
      )}
      {!data.type && (
        <div className="flex items-center justify-center h-[48px]">
          <SwatchIcon className="h-[30px] mr-3" />
          <Text>Select message type</Text>
        </div>
      )}

      {data.type === "media" && (
        <>
          {data.audio ? (
            <div className="row">
              <audio
                controls
                className="w-[80%] m-auto my-2"
                autoPlay={false}
                muted={false}
                alt={`Vocal`}
                src={data.audio}
              />
            </div>
          ) : (
            <>
              {data.body || data.image || data.video || data.document ? (
                <>
                  <div className="m-[3px]">
                    {data.image && (
                      <img
                        alt={`Image`}
                        width="100%"
                        style={{
                          maxHeight: "400px",
                          objectFit: "cover",
                          objectPosition: "center",
                          borderRadius: "10px",
                        }}
                        src={
                          data.image ||
                          "https://storage.gadget.dev/files/22129/42940/media/file/SuDoCyhFJkwqnGPOm30Of/5dd9eda2-817f-40c4-84d2-f5674654ec97"
                        }
                      />
                    )}

                    {data.document && (
                      <div className="w-full h-[230px] rounded-[10px] overflow-hidden scrollbar">
                        <iframe
                          alt={`Document`}
                          width="100%"
                          height="180px"
                          style={{
                            objectFit: "contain",
                            objectPosition: "center",
                            overflow: "hidden",
                            pointerEvents: "none",
                          }}
                          src={data.document}
                          type="application/pdf"
                        />
                        <div className="flex items-center justify-center h-[48px]">
                          <DocumentArrowUpIcon className="h-[30px] mr-3" />
                          <Text>Selected document PDF</Text>
                        </div>
                      </div>
                    )}
                    {data.video && (
                      <video
                        ref={videoRef}
                        alt={`Video`}
                        width="100%"
                        style={{
                          maxHeight: "400px",
                          objectFit: "cover",
                          objectPosition: "center",
                          borderRadius: "10px",
                        }}
                      >
                        <source src={data.video} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                  <div className="p-[10px] pt-0">
                    <>
                      {formatParagraph(data.body?.trim()).map(
                        (formattedLine, index) => (
                          <Text className="text-xs" as="div" key={index}>
                            {formattedLine}
                          </Text>
                        ),
                      )}
                      {data.footer && (
                        <Text className="text-xxs text-gray">
                          {data.footer}
                        </Text>
                      )}
                    </>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[48px]">
                  <ChatBubbleOvalLeftEllipsisIcon className="h-[30px] mr-3" />
                  <Text>Set your Message</Text>
                </div>
              )}
            </>
          )}
        </>
      )}

      {data.type === "text" && (data.video ?? "") !== "" && (
        <video
          width="100%"
          style={{
            maxHeight: "400px",
            objectFit: "cover",
            objectPosition: "center",
            borderRadius: "10px 10px 0 0",
          }}
        >
          <source
            src={
              data.video ||
              "https://storage.gadget.dev/files/22129/42940/media/file/xDycluQ_j76bA150Asp7G/2bc118d2-9e93-405c-9b5e-dced1b1051c0"
            }
          />
        </video>
      )}

      {/* This node type is deprecated and can not be created from now */}
      {data.type == "sticker" && (
        <img
          alt={`Image`}
          width="100%"
          style={{
            maxHeight: "400px",
            objectFit: "cover",
            objectPosition: "center",
            borderRadius: "10px",
          }}
          src={
            data.sticker ||
            "https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_image.png?alt=media&token=b42e2452-09c4-4076-9956-15520363da4b"
          }
        />
      )}

      {data.type === "list" &&
        (data.body ? (
          <div className="row">
            <>
              <div className="p-2">
                <Text variant="headerSm" fontWeight="bold">
                  {data.header}
                </Text>
                {formatParagraph(data.body?.trim()).map(
                  (formattedLine, index) => (
                    <Text className="text-xs" as="div" key={index}>
                      {formattedLine}
                    </Text>
                  ),
                )}
                <Text className="text-xxs text-gray">{data.footer}</Text>
                <br />
                <div style={{ textAlign: "center" }}>
                  <Text fontWeight="bold" className="text-xs">
                    {data.buttonTitle}
                  </Text>
                </div>
              </div>
            </>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[48px]">
            <ListBulletIcon className="h-[30px] mr-3" />
            <Text>Set your list here</Text>
          </div>
        ))}

      {data?.urlButton && (
        <div className="urlButton">
          <div />
          <Text className="text-xs">🔗 {data.urlButton.text}</Text>
          {data.urlClicks && data.urlButton?.trackingEnabled !== false ? (
            <div className="clicksBadge group">
              <Badge color="sky">
                <Text className="text-xs hidden group-hover:block">
                  ({data.urlClicks})
                </Text>
                <Text className="text-xs">
                  {Math.min(100, (data.urlClickRate * 100).toFixed(1))}%
                </Text>
              </Badge>
            </div>
          ) : (
            <div />
          )}
        </div>
      )}
      {data.buttons?.map((button, index) => (
        <div className="button" key={index}>
          <div />
          <Text className="text-xs">{button.title}</Text>
          {button.clicks ? (
            <div className="clicksBadge group">
              <Badge color="sky">
                <Text className="text-xs hidden group-hover:block">
                  ({button.clicks})
                </Text>
                <Text className="text-xs">
                  {Math.min(100, (button.clickRate * 100).toFixed(1))}%
                </Text>
              </Badge>
            </div>
          ) : (
            <div />
          )}
          <Handle
            type="source"
            id={handleIds[index]}
            position={Position.Right}
            style={{
              top: "auto",
              bottom: (data.buttons.length - index - 1) * 40 + 10,
              right: 5,
            }}
          />
        </div>
      ))}
      <Handle
        type="source"
        id="main"
        position={Position.Right}
        style={{
          top: `calc(50% - ${data.buttons?.length ?? 0} * 15px)`,
        }}
      />
    </div>
  );
}

export default MessageNode;
